import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Slider, ArrowSlider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Trash2, Search } from 'lucide-react';
import { getDeviceId, cacheDeviceStats, getCachedDeviceStats, dismissEmailPrompt } from '../utils/deviceTracking';
import AdminShortcut from '../components/AdminShortcut';

const SubmitReview = ({ user, setUser }) => {
  const navigate = useNavigate();
  const reviewSectionRef = useRef(null);
  const [formData, setFormData] = useState({
    restaurant_name: '',
    address: '',
    phone: '',
    city: '',
    state: '',
    overall_rating: 0,
    general_condition: '5',
    supplies: '5',
    maintenance: '5',
    restroom_types: [],
    comments: '',
    fun_url: ''
  });
  const [restaurantSuggestions, setRestaurantSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [soundSettings, setSoundSettings] = useState(null);
  const [animatingButton, setAnimatingButton] = useState(null);
  const [callSettings, setCallSettings] = useState(null);
  const [callingAlert, setCallingAlert] = useState(null);
  
  // Delete account state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  
  // Quick Alert Modal state (for non-logged-in users)
  const [showQuickAlertModal, setShowQuickAlertModal] = useState(false);
  const [quickAlertData, setQuickAlertData] = useState({
    restaurant_name: '',
    address: '',
    phone: '',
    city: '',
    state: ''
  });
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [showReviewThankYou, setShowReviewThankYou] = useState(false);
  const [thankYouPhase, setThankYouPhase] = useState(0);
  const [sendingQuickAlert, setSendingQuickAlert] = useState(false);
  const [quickAlertSuggestions, setQuickAlertSuggestions] = useState([]);
  const [showQuickAlertSuggestions, setShowQuickAlertSuggestions] = useState(false);
  
  // Admin login modal state
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loggingIn, setLoggingIn] = useState(false);
  
  // Device tracking state
  const [deviceId, setDeviceId] = useState(null);
  const [deviceStats, setDeviceStats] = useState(null);
  const [showEmailPromptModal, setShowEmailPromptModal] = useState(false);
  const [emailLinkData, setEmailLinkData] = useState({ name: '', email: '', password: '' });
  const [linkingEmail, setLinkingEmail] = useState(false);
  
  // User location state for local restaurant search
  const [userLocation, setUserLocation] = useState(null);

  useEffect(() => {
    // Get user's location for local restaurant search priority
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          console.log('Location obtained for local search:', position.coords.latitude, position.coords.longitude);
        },
        (error) => {
          console.log('Geolocation not available or denied:', error.message);
          // Continue without location - search will work but not prioritize local results
        },
        { enableHighAccuracy: false, timeout: 5000, maximumAge: 300000 }
      );
    }
  }, []);

  useEffect(() => {
    // Initialize device tracking
    const initDevice = async () => {
      const id = getDeviceId();
      setDeviceId(id);
      
      try {
        const response = await axios.post(`${API}/device/register`, { device_id: id });
        setDeviceStats(response.data);
        cacheDeviceStats(response.data);
        
        // Check if we should prompt for email (after 5 contributions)
        if (response.data.should_prompt_email && !user) {
          setShowEmailPromptModal(true);
        }
      } catch (error) {
        console.error('Failed to register device:', error);
        // Use cached stats as fallback
        const cached = getCachedDeviceStats();
        if (cached) setDeviceStats(cached);
      }
    };
    
    initDevice();
  }, [user]);

  useEffect(() => {
    // Fetch sound settings on component mount with cache-busting
    const fetchSounds = async () => {
      try {
        const response = await axios.get(`${API}/sounds?t=${Date.now()}`);
        setSoundSettings(response.data);
      } catch (error) {
        console.error('Failed to load sound settings:', error);
      }
    };
    
    // Fetch call settings
    const fetchCallSettings = async () => {
      try {
        const response = await axios.get(`${API}/calls/settings`);
        setCallSettings(response.data);
      } catch (error) {
        console.error('Failed to load call settings:', error);
      }
    };
    
    // Load pending restaurant info from quick alert (after login/signup)
    const pendingRestaurant = sessionStorage.getItem('pendingReviewRestaurant');
    if (pendingRestaurant) {
      try {
        const restaurantData = JSON.parse(pendingRestaurant);
        setFormData(prev => ({
          ...prev,
          restaurant_name: restaurantData.restaurant_name || '',
          address: restaurantData.address || '',
          phone: restaurantData.phone || ''
        }));
        sessionStorage.removeItem('pendingReviewRestaurant');
        toast.success('Restaurant info loaded! Complete your review below.');
      } catch (e) {
        console.error('Failed to load pending restaurant:', e);
      }
    }
    
    fetchSounds();
    fetchCallSettings();
  }, [user]);

  // Handle alert button clicks
  const handleTriggerAlert = async (messageType) => {
    if (!formData.phone) {
      toast.error('Please select a restaurant with a phone number first');
      return;
    }
    
    setCallingAlert(messageType);
    try {
      await axios.post(`${API}/calls/trigger-direct`, {
        phone_number: formData.phone,
        message_type: messageType,
        restaurant_name: formData.restaurant_name
      });
      toast.success('Alert call initiated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to trigger alert');
    } finally {
      setCallingAlert(null);
    }
  };
  
  // Handle quick alert for non-logged-in users
  const handleQuickAlert = async () => {
    if (!quickAlertData.phone || !quickAlertData.restaurant_name) {
      toast.error('Please enter restaurant name and phone number');
      return;
    }
    
    setSendingQuickAlert(true);
    try {
      await axios.post(`${API}/calls/trigger-anonymous`, {
        phone_number: quickAlertData.phone,
        message_type: 'needs_attention',
        restaurant_name: quickAlertData.restaurant_name,
        device_id: deviceId  // Track device contributions
      });
      
      sessionStorage.setItem('pendingReviewRestaurant', JSON.stringify({
        restaurant_name: quickAlertData.restaurant_name,
        address: quickAlertData.address,
        phone: quickAlertData.phone,
        city: quickAlertData.city,
        state: quickAlertData.state
      }));
      
      // Update device stats after alert
      if (deviceId) {
        try {
          const statsResponse = await axios.get(`${API}/device/${deviceId}/stats`);
          setDeviceStats(statsResponse.data);
          cacheDeviceStats(statsResponse.data);
        } catch (e) {
          console.error('Failed to update device stats:', e);
        }
      }
      
      setShowQuickAlertModal(false);
      setShowThankYouModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send alert');
    } finally {
      setSendingQuickAlert(false);
    }
  };
  
  // Handle restaurant name change in quick alert modal
  const handleQuickAlertRestaurantChange = async (value) => {
    setQuickAlertData({...quickAlertData, restaurant_name: value});
    
    if (value.length >= 3) {
      try {
        // Use anonymous search endpoint for quick alerts with location bias
        let url = `${API}/restaurants/search/anonymous?q=${encodeURIComponent(value)}`;
        if (userLocation) {
          url += `&lat=${userLocation.lat}&lng=${userLocation.lng}`;
        }
        const response = await axios.get(url);
        setQuickAlertSuggestions(response.data);
        setShowQuickAlertSuggestions(response.data.length > 0);
      } catch (error) {
        console.error('Failed to fetch restaurant suggestions:', error);
        setQuickAlertSuggestions([]);
        setShowQuickAlertSuggestions(false);
      }
    } else {
      setQuickAlertSuggestions([]);
      setShowQuickAlertSuggestions(false);
    }
  };
  
  // Handle selecting a restaurant from quick alert suggestions
  const handleSelectQuickAlertRestaurant = (restaurant) => {
    setQuickAlertData({
      restaurant_name: restaurant.restaurant_name,
      address: restaurant.address || '',
      phone: restaurant.phone || '',
      city: restaurant.city || '',
      state: restaurant.state || ''
    });
    setShowQuickAlertSuggestions(false);
    setQuickAlertSuggestions([]);
  };

  const handleRestaurantNameChange = async (value) => {
    setFormData({...formData, restaurant_name: value});
    
    if (value.length >= 3) {
      try {
        const token = localStorage.getItem('token');
        // Build URL with location bias for local results priority
        let baseUrl = token 
          ? `${API}/restaurants/search?q=${encodeURIComponent(value)}`
          : `${API}/restaurants/search/anonymous?q=${encodeURIComponent(value)}`;
        
        if (userLocation) {
          baseUrl += `&lat=${userLocation.lat}&lng=${userLocation.lng}`;
        }
        
        const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
        
        const response = await axios.get(baseUrl, config);
        setRestaurantSuggestions(response.data);
        setShowSuggestions(response.data.length > 0);
      } catch (error) {
        console.error('Failed to fetch restaurant suggestions:', error);
        setRestaurantSuggestions([]);
        setShowSuggestions(false);
      }
    } else {
      setRestaurantSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelectRestaurant = (restaurant) => {
    setFormData({
      ...formData,
      restaurant_name: restaurant.restaurant_name,
      address: restaurant.address || '',
      phone: restaurant.phone || '',
      city: restaurant.city || '',
      state: restaurant.state || ''
    });
    setShowSuggestions(false);
    setRestaurantSuggestions([]);
  };

  // Admin login handler
  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      localStorage.setItem('token', response.data.access_token);
      if (setUser) setUser(response.data.user);
      toast.success('Login successful!');
      setShowLoginModal(false);
      setLoginData({ email: '', password: '' });
      // Navigate to admin if user is admin, otherwise reload
      if (response.data.user?.is_admin) {
        navigate('/admin');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoggingIn(false);
    }
  };

  const handleRatingButtonClick = (rating, soundUrl) => {
    setFormData({...formData, overall_rating: rating});
    setAnimatingButton(rating);
    setTimeout(() => setAnimatingButton(null), 500);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.restaurant_name.trim()) {
      toast.error('Please enter a restaurant name');
      return;
    }
    if (formData.overall_rating === 0) {
      toast.error('Please select your overall experience rating');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (token) {
        // Logged-in user - use authenticated endpoint
        await axios.post(
          `${API}/reviews`,
          {
            restaurant_name: formData.restaurant_name,
            address: formData.address,
            phone: formData.phone,
            city: formData.city,
            state: formData.state,
            overall_rating: formData.overall_rating,
            general_condition: parseInt(formData.general_condition),
            supplies: parseInt(formData.supplies),
            maintenance: parseInt(formData.maintenance),
            restroom_types: formData.restroom_types,
            comments: formData.comments,
            fun_url: formData.fun_url
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // Anonymous user - use device ID endpoint
        await axios.post(`${API}/reviews/anonymous`, {
          device_id: deviceId,
          restaurant_name: formData.restaurant_name,
          address: formData.address,
          phone: formData.phone,
          city: formData.city,
          state: formData.state,
          overall_rating: formData.overall_rating,
          general_condition: parseInt(formData.general_condition),
          supplies: parseInt(formData.supplies),
          maintenance: parseInt(formData.maintenance),
          restroom_types: formData.restroom_types,
          comments: formData.comments,
          fun_url: formData.fun_url
        });
        
        // Update device stats
        const statsResponse = await axios.get(`${API}/device/${deviceId}/stats`);
        setDeviceStats(statsResponse.data);
        cacheDeviceStats(statsResponse.data);
        
        // Check if we should prompt for email
        if (statsResponse.data.should_prompt_email) {
          setShowEmailPromptModal(true);
        }
      }
      
      toast.success('Review submitted successfully!');
      
      // Show the dramatic Thank You animation sequence - doubled timing for impact
      setShowReviewThankYou(true);
      setThankYouPhase(1);
      
      // Phase 2: "now WE can" + KNOW BEFORE WE GO image
      setTimeout(() => setThankYouPhase(2), 3000);
      
      // Phase 3: Golden plunger reveal (skip Thanks to YOU)
      setTimeout(() => setThankYouPhase(3), 6000);
      
      // Reset form
      setFormData({
        restaurant_name: '',
        address: '',
        phone: '',
        city: '',
        state: '',
        overall_rating: 0,
        general_condition: '5',
        supplies: '5',
        maintenance: '5',
        restroom_types: [],
        comments: '',
        fun_url: ''
      });
      
      // Navigate after showing the full animation (12 seconds total)
      setTimeout(() => {
        setShowReviewThankYou(false);
        setThankYouPhase(0);
        navigate('/dashboard');
      }, 12000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please log in to delete your account');
      return;
    }
    
    setDeletingAccount(true);
    try {
      await axios.post(
        `${API}/auth/delete-account`,
        { password: deletePassword, confirmation: deleteConfirmation },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Your account has been permanently deleted');
      localStorage.removeItem('token');
      setUser(null);
      setShowDeleteDialog(false);
      window.location.href = '/';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    } finally {
      setDeletingAccount(false);
    }
  };

  const scrollToReview = () => {
    if (reviewSectionRef.current) {
      reviewSectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Handle linking device to email account
  const handleLinkEmail = async () => {
    if (!emailLinkData.name || !emailLinkData.email || !emailLinkData.password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setLinkingEmail(true);
    try {
      const response = await axios.post(`${API}/device/link-email`, {
        device_id: deviceId,
        name: emailLinkData.name,
        email: emailLinkData.email,
        password: emailLinkData.password
      });
      
      // Store token and update user
      localStorage.setItem('token', response.data.access_token);
      if (setUser) setUser(response.data.user);
      
      toast.success(response.data.message || 'Account created! Your reviews have been saved.');
      setShowEmailPromptModal(false);
      dismissEmailPrompt();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setLinkingEmail(false);
    }
  };

  const handleDismissEmailPrompt = async () => {
    setShowEmailPromptModal(false);
    dismissEmailPrompt();
    
    // Tell backend we dismissed the prompt
    if (deviceId) {
      try {
        await axios.post(`${API}/device/${deviceId}/dismiss-email-prompt`);
      } catch (e) {
        console.error('Failed to dismiss prompt:', e);
      }
    }
  };

  return (
    <div className="min-h-screen bg-yellow-400 text-gray-900">
      {/* Hero Section - Matching the design */}
      <div className="bg-yellow-400 px-4 pt-6 pb-4">
        <div className="max-w-md mx-auto flex flex-col items-center">
          {/* Original Logo Image with TM positioned above and right of REPORT */}
          <div className="relative mb-4 w-full">
            <img 
              src="https://customer-assets.emergentagent.com/job_loo-tracker/artifacts/2igfkpva_Background%20for%20APP%20Home%20Screen%20no%20head.jpg" 
              alt="The Loo Report - Know Before You Go!"
              className="w-full h-auto object-contain"
            />
            <span className="absolute text-black text-base font-bold" style={{ fontFamily: 'Arial, sans-serif', bottom: '25%', right: '5%' }}>™</span>
          </div>

          {/* Navigation Buttons - Stacked vertically, same width as review boxes */}
          <div className="w-full space-y-3">
            {/* Search Restaurants Button - Goes directly to dashboard with search */}
            <Button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-white hover:bg-gray-100 text-gray-700 font-bold py-4 px-6 rounded-full border-2 border-gray-300 shadow-md flex items-center justify-center gap-2"
              style={{ fontFamily: 'Fredoka, sans-serif' }}
              data-testid="search-restaurants-btn"
            >
              <Search className="w-5 h-5" />
              SEARCH RESTAURANTS
            </Button>

            {/* Post a Review Button */}
            <Button
              onClick={scrollToReview}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-4 px-6 rounded-full border-2 border-blue-700 shadow-md text-lg"
              style={{ fontFamily: 'Fredoka, sans-serif', letterSpacing: '0.05em' }}
              data-testid="post-review-btn"
            >
              POST A REVIEW
            </Button>

            {/* View Reviews Button */}
            <Button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-4 px-6 rounded-full border-2 border-yellow-700 shadow-md text-lg"
              style={{ fontFamily: 'Fredoka, sans-serif', letterSpacing: '0.05em' }}
              data-testid="view-reviews-btn"
            >
              VIEW REVIEWS
            </Button>

            {/* Urgent Alert Button */}
            <Button
              onClick={() => setShowQuickAlertModal(true)}
              className="w-full bg-red-500 hover:bg-red-600 text-white font-bold py-5 px-6 rounded-full border-2 border-red-700 shadow-md"
              style={{ fontFamily: 'Fredoka, sans-serif', minHeight: '70px' }}
              data-testid="urgent-alert-btn"
            >
              <div className="flex flex-col items-center leading-tight">
                <span className="text-lg font-bold">URGENT ALERT</span>
                <span className="text-sm opacity-90">REPORT A PROBLEM NOW</span>
              </div>
            </Button>
          </div>

          {/* Admin Login - Small link for admin access */}
          {!user && (
            <div className="mt-4 text-center">
              <button
                onClick={() => setShowLoginModal(true)}
                className="text-gray-500 hover:text-gray-700 text-xs underline"
                style={{ fontFamily: 'Fredoka, sans-serif' }}
                data-testid="admin-login-link"
              >
                Admin Login
              </button>
            </div>
          )}

          {/* Admin button only - No Login/Register for device-based system */}
          {user && user.is_admin && (
            <div className="mt-4 flex justify-center gap-2">
              <Button
                onClick={() => navigate('/admin')}
                className="bg-red-600 hover:bg-red-700 text-white font-bold border-2 border-black px-4 py-2"
                style={{ fontFamily: 'Fredoka, sans-serif' }}
                data-testid="admin-btn"
              >
                ⚙️ Admin Panel
              </Button>
              <Button
                onClick={() => {
                  localStorage.removeItem('token');
                  window.location.reload();
                }}
                variant="outline"
                className="bg-white hover:bg-gray-100 text-gray-600 font-bold border-2 border-gray-400 px-4 py-2"
                style={{ fontFamily: 'Fredoka, sans-serif' }}
                data-testid="logout-btn"
              >
                Logout
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Review Form Section - Now available for everyone */}
      <div ref={reviewSectionRef} className="bg-gradient-to-b from-yellow-400 to-yellow-300 px-4 pb-8">
        <div className="max-w-md mx-auto">
          <form onSubmit={handleSubmit} className="space-y-3">
            {/* Restaurant Info - First (below URGENT ALERT) */}
            <div className="bg-white p-3 rounded-lg border-3 border-black shadow-md space-y-2">
              <div className="relative">
                <Label htmlFor="restaurant" className="text-red-600 text-xs font-bold block"
                       style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  Restaurant Name *
                </Label>
                <Input
                  id="restaurant"
                  type="text"
                  value={formData.restaurant_name}
                  onChange={(e) => handleRestaurantNameChange(e.target.value)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  required
                  className="mt-1 bg-white border-2 border-gray-300 text-black text-sm h-9"
                  placeholder="Start typing restaurant name..."
                  data-testid="restaurant-name-input"
                  autoComplete="off"
                />
                {showSuggestions && restaurantSuggestions.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border-2 border-black rounded shadow-lg max-h-48 overflow-y-auto">
                    {restaurantSuggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        onClick={() => handleSelectRestaurant(suggestion)}
                        className="p-2 hover:bg-yellow-100 cursor-pointer border-b border-gray-200 text-sm"
                      >
                        <div className="font-bold text-gray-900">{suggestion.restaurant_name}</div>
                        {suggestion.address && <div className="text-xs text-gray-600">{suggestion.address}</div>}
                        {suggestion.phone && <div className="text-xs text-blue-600">{suggestion.phone}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <Input
                  id="address"
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  className="bg-white border-2 border-gray-300 text-black text-sm h-9"
                  placeholder="Address"
                  data-testid="address-input"
                />
              </div>

              <div>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="bg-white border-2 border-gray-300 text-black text-sm h-9"
                  placeholder="Telephone"
                  data-testid="phone-input"
                />
              </div>
            </div>

            {/* Rating Section - No eyeballs, single line title, ratings in one row */}
            <div className="bg-white p-3 rounded-lg border-3 border-black shadow-md">
              <h3 className="text-center text-red-600 text-sm font-bold mb-2"
                  style={{ fontFamily: 'Fredoka, sans-serif', letterSpacing: '0.03em' }}>
                RATE YOUR OVERALL EXPERIENCE
              </h3>
              
              {/* Ratings in a single row - 4 across, larger buttons */}
              <div className="flex justify-center gap-1">
                {/* EXCEPTIONAL */}
                <button
                  type="button"
                  onClick={() => handleRatingButtonClick(4, soundSettings?.excellent_sound)}
                  className={`flex flex-col items-center p-2 rounded-lg border-2 border-black transition-all duration-200 w-[92px] ${
                    formData.overall_rating === 4 
                      ? 'bg-green-500 text-white scale-105 shadow-lg' 
                      : 'bg-white hover:bg-green-100'
                  }`}
                  data-testid="rating-exceptional"
                >
                  <img 
                    src="https://customer-assets.emergentagent.com/job_ab3cc9c0-1e7a-4d44-b103-5b0d8320f833/artifacts/wear2a7c_IMG_3052.jpeg" 
                    alt="Exceptional"
                    className="w-10 h-10 object-contain"
                  />
                  <span className="text-[10px] font-black mt-1.5 leading-none uppercase" style={{ fontFamily: 'Arial Black, sans-serif', letterSpacing: '0.05em' }}>EXCEPTIONAL</span>
                </button>

                {/* IT'S OK */}
                <button
                  type="button"
                  onClick={() => handleRatingButtonClick(3, soundSettings?.good_sound)}
                  className={`flex flex-col items-center p-2 rounded-lg border-2 border-black transition-all duration-200 w-[92px] ${
                    formData.overall_rating === 3 
                      ? 'bg-green-500 text-white scale-105 shadow-lg' 
                      : 'bg-white hover:bg-green-100'
                  }`}
                  data-testid="rating-good"
                >
                  <span className="text-[32px] leading-none">👌</span>
                  <span className="text-[11px] font-black mt-1.5 leading-none uppercase" style={{ fontFamily: 'Arial Black, sans-serif', letterSpacing: '0.02em' }}>IT'S OK</span>
                </button>

                {/* NEEDS ATTENTION */}
                <button
                  type="button"
                  onClick={() => handleRatingButtonClick(2, soundSettings?.bad_sound)}
                  className={`flex flex-col items-center p-2 rounded-lg border-2 border-black transition-all duration-200 w-[92px] ${
                    formData.overall_rating === 2 
                      ? 'bg-red-500 text-white scale-105 shadow-lg' 
                      : 'bg-white hover:bg-red-100'
                  }`}
                  data-testid="rating-needs-attention"
                >
                  <img 
                    src="https://static.thenounproject.com/png/5000928-200.png" 
                    alt="Needs Attention"
                    className="w-10 h-10 object-contain"
                  />
                  <span className="text-[11px] font-black mt-1.5 leading-tight text-center uppercase" style={{ fontFamily: 'Arial Black, sans-serif', letterSpacing: '0.02em' }}>NEEDS<br/>ATTENTION</span>
                </button>

                {/* MAJOR ISSUES */}
                <button
                  type="button"
                  onClick={() => handleRatingButtonClick(1, soundSettings?.awful_sound)}
                  className={`flex flex-col items-center p-2 rounded-lg border-2 border-black transition-all duration-200 w-[92px] ${
                    formData.overall_rating === 1 
                      ? 'bg-red-500 text-white scale-105 shadow-lg' 
                      : 'bg-white hover:bg-red-100'
                  }`}
                  data-testid="rating-major-issues"
                >
                  <img 
                    src="https://customer-assets.emergentagent.com/job_ab3cc9c0-1e7a-4d44-b103-5b0d8320f833/artifacts/o44vc304_IMG_3041.jpeg" 
                    alt="Major Issues"
                    className="w-10 h-10 object-contain"
                  />
                  <span className="text-[11px] font-black mt-1.5 leading-tight text-center uppercase" style={{ fontFamily: 'Arial Black, sans-serif', letterSpacing: '0.02em' }}>MAJOR<br/>ISSUES</span>
                </button>
              </div>
            </div>

            {/* Detailed Ratings - Compact */}
            <div className="bg-white p-3 rounded-lg border-3 border-black shadow-md">
              <h4 className="text-red-600 text-xs font-bold text-center mb-3" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                DETAILED RATINGS
              </h4>
              
              <div className="space-y-4">
                {/* Cleanliness */}
                <div>
                  <p className="text-center text-xs font-bold text-gray-800 mb-1" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                    CLEANLINESS
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-gray-700 w-14 text-right" style={{ fontFamily: 'Fredoka, sans-serif' }}>Nasty</span>
                    <ArrowSlider
                      value={[parseInt(formData.general_condition) || 5]}
                      onValueChange={(value) => setFormData({...formData, general_condition: value[0].toString()})}
                      min={1}
                      max={10}
                      step={1}
                      className="flex-1"
                      data-testid="general-condition-slider"
                    />
                    <span className="text-xs font-bold text-gray-700 w-14" style={{ fontFamily: 'Fredoka, sans-serif' }}>Spotless</span>
                  </div>
                </div>

                {/* Supplies */}
                <div>
                  <p className="text-center text-xs font-bold text-gray-800 mb-1" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                    SUPPLIES
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-gray-700 w-14 text-right" style={{ fontFamily: 'Fredoka, sans-serif' }}>BYO</span>
                    <ArrowSlider
                      value={[parseInt(formData.supplies) || 5]}
                      onValueChange={(value) => setFormData({...formData, supplies: value[0].toString()})}
                      min={1}
                      max={10}
                      step={1}
                      className="flex-1"
                      data-testid="supplies-slider"
                    />
                    <span className="text-xs font-bold text-gray-700 w-14" style={{ fontFamily: 'Fredoka, sans-serif' }}>Great</span>
                  </div>
                </div>

                {/* Maintenance */}
                <div>
                  <p className="text-center text-xs font-bold text-gray-800 mb-1" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                    MAINTENANCE
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-gray-700 w-14 text-right" style={{ fontFamily: 'Fredoka, sans-serif' }}>Broken</span>
                    <ArrowSlider
                      value={[parseInt(formData.maintenance) || 5]}
                      onValueChange={(value) => setFormData({...formData, maintenance: value[0].toString()})}
                      min={1}
                      max={10}
                      step={1}
                      className="flex-1"
                      data-testid="maintenance-slider"
                    />
                    <span className="text-xs font-bold text-gray-700 w-14" style={{ fontFamily: 'Fredoka, sans-serif' }}>Perfect</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Available Restrooms - Reordered */}
            <div className="bg-white p-3 rounded-lg border-3 border-black shadow-md">
              <Label className="text-red-600 text-xs font-bold block mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                AVAILABLE RESTROOMS
              </Label>
              <div className="grid grid-cols-3 gap-2">
                {/* Row 1: Gender Neutral, Men, Women */}
                <label className="flex items-center gap-1.5 text-xs">
                  <Checkbox
                    checked={formData.restroom_types.includes('Gender Neutral')}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setFormData({...formData, restroom_types: [...formData.restroom_types, 'Gender Neutral']});
                      } else {
                        setFormData({...formData, restroom_types: formData.restroom_types.filter(t => t !== 'Gender Neutral')});
                      }
                    }}
                    data-testid="restroom-type-gender-neutral"
                  />
                  <span style={{ fontFamily: 'Fredoka, sans-serif' }}>Gender Neutral</span>
                </label>
                <label className="flex items-center gap-1.5 text-xs">
                  <Checkbox
                    checked={formData.restroom_types.includes('Men')}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setFormData({...formData, restroom_types: [...formData.restroom_types, 'Men']});
                      } else {
                        setFormData({...formData, restroom_types: formData.restroom_types.filter(t => t !== 'Men')});
                      }
                    }}
                    data-testid="restroom-type-men"
                  />
                  <span style={{ fontFamily: 'Fredoka, sans-serif' }}>Men</span>
                </label>
                <label className="flex items-center gap-1.5 text-xs">
                  <Checkbox
                    checked={formData.restroom_types.includes('Women')}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setFormData({...formData, restroom_types: [...formData.restroom_types, 'Women']});
                      } else {
                        setFormData({...formData, restroom_types: formData.restroom_types.filter(t => t !== 'Women')});
                      }
                    }}
                    data-testid="restroom-type-women"
                  />
                  <span style={{ fontFamily: 'Fredoka, sans-serif' }}>Women</span>
                </label>
                {/* Row 2: Family, Wheelchair Accessible */}
                <label className="flex items-center gap-1.5 text-xs">
                  <Checkbox
                    checked={formData.restroom_types.includes('Family')}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setFormData({...formData, restroom_types: [...formData.restroom_types, 'Family']});
                      } else {
                        setFormData({...formData, restroom_types: formData.restroom_types.filter(t => t !== 'Family')});
                      }
                    }}
                    data-testid="restroom-type-family"
                  />
                  <span style={{ fontFamily: 'Fredoka, sans-serif' }}>Family</span>
                </label>
                <label className="flex items-center gap-1.5 text-xs col-span-2">
                  <Checkbox
                    checked={formData.restroom_types.includes('Wheelchair Accessible')}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setFormData({...formData, restroom_types: [...formData.restroom_types, 'Wheelchair Accessible']});
                      } else {
                        setFormData({...formData, restroom_types: formData.restroom_types.filter(t => t !== 'Wheelchair Accessible')});
                      }
                    }}
                    data-testid="restroom-type-wheelchair-accessible"
                  />
                  <span style={{ fontFamily: 'Fredoka, sans-serif' }}>Wheelchair Accessible</span>
                </label>
              </div>
            </div>

            {/* Comments */}
            <div className="bg-white p-3 rounded-lg border-3 border-black shadow-md">
              <Label htmlFor="comments" className="text-red-600 text-xs font-bold block mb-1"
                     style={{ fontFamily: 'Fredoka, sans-serif' }}>
                Comments (Optional)
              </Label>
              <Textarea
                id="comments"
                value={formData.comments}
                onChange={(e) => setFormData({...formData, comments: e.target.value})}
                className="mt-1 bg-white border-2 border-gray-300 text-black text-sm"
                placeholder="Share your experience..."
                rows={2}
                data-testid="comments-input"
              />
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 text-lg border-2 border-green-700 rounded-lg shadow-md"
              style={{ fontFamily: 'Fredoka, sans-serif' }}
              data-testid="submit-review-btn"
            >
              {loading ? 'SUBMITTING...' : 'SUBMIT REVIEW'}
            </Button>
          </form>
        </div>
      </div>

      {/* Delete Account Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="border-4 border-black bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-red-600" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Delete Account
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              This action is permanent and cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="delete-password" className="font-bold text-gray-700">
                Enter your password
              </Label>
              <Input
                id="delete-password"
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="Password"
                className="mt-1 border-2 border-black"
              />
            </div>
            <div>
              <Label htmlFor="delete-confirm" className="font-bold text-gray-700">
                Type &quot;DELETE&quot; to confirm
              </Label>
              <Input
                id="delete-confirm"
                type="text"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder="DELETE"
                className="mt-1 border-2 border-black"
              />
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => setShowDeleteDialog(false)}
                variant="outline"
                className="flex-1 border-2 border-gray-300"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteAccount}
                disabled={deletingAccount || deleteConfirmation !== 'DELETE'}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white font-bold border-2 border-red-700"
              >
                {deletingAccount ? 'Deleting...' : 'Delete Account'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Alert Modal */}
      <Dialog open={showQuickAlertModal} onOpenChange={setShowQuickAlertModal}>
        <DialogContent className="border-4 border-black bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-red-600" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              📞 URGENT ALERT
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              Anonymously alert a restaurant about restroom issues
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="relative">
              <Label htmlFor="quick-restaurant" className="font-bold text-gray-700">
                Restaurant Name *
              </Label>
              <Input
                id="quick-restaurant"
                value={quickAlertData.restaurant_name}
                onChange={(e) => handleQuickAlertRestaurantChange(e.target.value)}
                placeholder="Start typing restaurant name..."
                className="mt-1 border-2 border-black"
                data-testid="quick-alert-restaurant"
                autoComplete="off"
              />
              {showQuickAlertSuggestions && quickAlertSuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border-2 border-black rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {quickAlertSuggestions.map((restaurant, index) => (
                    <div
                      key={index}
                      onClick={() => handleSelectQuickAlertRestaurant(restaurant)}
                      className="p-2 hover:bg-yellow-100 cursor-pointer border-b border-gray-200 text-sm"
                    >
                      <p className="font-bold text-gray-800">{restaurant.restaurant_name}</p>
                      <p className="text-xs text-gray-600">{restaurant.address}</p>
                      {restaurant.phone && (
                        <p className="text-xs text-green-600">📞 {restaurant.phone}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="quick-address" className="font-bold text-gray-700">
                Address
              </Label>
              <Input
                id="quick-address"
                value={quickAlertData.address}
                onChange={(e) => setQuickAlertData({...quickAlertData, address: e.target.value})}
                placeholder="Enter address or select from above"
                className="mt-1 border-2 border-black"
                data-testid="quick-alert-address"
              />
            </div>
            <div>
              <Label htmlFor="quick-phone" className="font-bold text-gray-700">
                Telephone Number *
              </Label>
              <Input
                id="quick-phone"
                type="tel"
                value={quickAlertData.phone}
                onChange={(e) => setQuickAlertData({...quickAlertData, phone: e.target.value})}
                placeholder="Enter phone number or select from above"
                className="mt-1 border-2 border-black"
                data-testid="quick-alert-phone"
              />
            </div>
            <Button
              onClick={handleQuickAlert}
              disabled={sendingQuickAlert || !quickAlertData.restaurant_name || !quickAlertData.phone}
              className="w-full bg-red-500 hover:bg-red-600 text-white font-bold text-lg py-6 border-2 border-black"
              style={{ fontFamily: 'Fredoka, sans-serif' }}
              data-testid="send-quick-alert-btn"
            >
              {sendingQuickAlert ? 'SENDING...' : 'SEND THE ALERT!'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Thank You Modal */}
      {/* Review Submission - Comic Book Style Celebration */}
      {showReviewThankYou && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden">
          {/* Bright yellow background matching app style */}
          <div className="absolute inset-0 bg-yellow-400" />
          
          {/* Toilet paper and plunger confetti */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {[...Array(25)].map((_, i) => (
              <div
                key={`tp-${i}`}
                className="absolute animate-fall-spin"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: '-50px',
                  animationDelay: `${Math.random() * 2}s`,
                  animationDuration: `${3 + Math.random() * 2}s`,
                }}
              >
                <span className="text-3xl">{['🧻', '🪠', '💧', '⭐', '✨'][i % 5]}</span>
              </div>
            ))}
          </div>
          
          {/* Comic burst background effects */}
          <div className="absolute inset-0 pointer-events-none">
            {/* Starburst lines */}
            <div 
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[150%] h-[150%]"
              style={{
                background: 'repeating-conic-gradient(from 0deg, #FCD34D 0deg 10deg, #FBBF24 10deg 20deg)',
                opacity: 0.3
              }}
            />
          </div>
          
          {/* Main content */}
          <div className="relative z-10 text-center px-4 max-w-md">
            {/* Phase 1: THANK YOU! with comic burst */}
            {thankYouPhase >= 1 && (
              <div className="transform transition-all duration-500 animate-bounce-in">
                {/* Comic burst background */}
                <div className="relative inline-block mb-4">
                  <div 
                    className="absolute inset-0 -m-8"
                    style={{
                      background: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 100 100\'%3E%3Cpolygon points=\'50,0 61,35 98,35 68,57 79,91 50,70 21,91 32,57 2,35 39,35\' fill=\'%23FF0000\'/%3E%3C/svg%3E")',
                      backgroundSize: 'contain',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'center',
                      transform: 'scale(2)',
                      opacity: 0.15
                    }}
                  />
                  <h1 
                    className="text-6xl md:text-7xl font-black text-red-600 relative"
                    style={{ 
                      fontFamily: 'Fredoka, sans-serif',
                      textShadow: '4px 4px 0px #000, -2px -2px 0px #FFF',
                      WebkitTextStroke: '2px black'
                    }}
                  >
                    THANK YOU!
                  </h1>
                </div>
              </div>
            )}
            
            {/* Phase 2: KNOW BEFORE YOU GO */}
            {thankYouPhase >= 2 && (
              <div className="transform transition-all duration-500 animate-slide-up">
                {/* Comic style KNOW BEFORE YOU GO box */}
                <div 
                  className="relative inline-block bg-white border-4 border-black rounded-lg p-4 mb-4 transform -rotate-1"
                  style={{ boxShadow: '6px 6px 0px #000' }}
                >
                  <p 
                    className="text-3xl md:text-4xl font-black text-red-600 uppercase"
                    style={{ 
                      fontFamily: 'Impact, sans-serif',
                      letterSpacing: '0.02em'
                    }}
                  >
                    KNOW BEFORE
                  </p>
                  <p 
                    className="text-3xl md:text-4xl font-black text-red-600 uppercase"
                    style={{ 
                      fontFamily: 'Impact, sans-serif',
                      letterSpacing: '0.02em'
                    }}
                  >
                    YOU GO!
                  </p>
                </div>
              </div>
            )}
            
            {/* Phase 3: Cartoon Golden Plunger Award */}
            {thankYouPhase >= 3 && (
              <div className="transform transition-all duration-700 animate-trophy-pop mt-6">
                {/* Kerching sound effect - plays when golden plunger appears */}
                <audio 
                  autoPlay 
                  src="https://cdn.pixabay.com/audio/2022/03/10/audio_b4c5e29c6e.mp3"
                  style={{ display: 'none' }}
                />
                
                {/* Trophy container with comic style */}
                <div className="relative">
                  {/* Sparkle effects around trophy */}
                  <div className="absolute inset-0 animate-pulse">
                    <span className="absolute -top-4 left-1/4 text-3xl">✨</span>
                    <span className="absolute -top-2 right-1/4 text-2xl">⭐</span>
                    <span className="absolute top-1/4 -left-4 text-2xl">✨</span>
                    <span className="absolute top-1/4 -right-4 text-3xl">⭐</span>
                  </div>
                  
                  {/* Cartoon Golden Plunger */}
                  <div className="bg-white rounded-xl p-2 inline-block border-4 border-black" style={{ boxShadow: '4px 4px 0px #000' }}>
                    <img 
                      src="https://static.prod-images.emergentagent.com/jobs/0e34189e-8508-48c0-a7fb-ee81ee3901e5/images/eac9f95ece8eb86fdef2bdf37a9c7b5fc9a968ecf2f811921a2338a903a402e6.jpeg"
                      alt="Golden Plunger Award"
                      className="w-44 h-44 md:w-52 md:h-52 mx-auto object-contain animate-wiggle"
                    />
                  </div>
                </div>
                
                {/* Award text - comic style */}
                <div 
                  className="mt-4 bg-white border-4 border-black rounded-xl p-4 transform rotate-1"
                  style={{ boxShadow: '5px 5px 0px #000' }}
                >
                  <p 
                    className="text-xl md:text-2xl font-black text-black uppercase"
                    style={{ fontFamily: 'Fredoka, sans-serif' }}
                  >
                    You earned a
                  </p>
                  <p 
                    className="text-3xl md:text-4xl font-black text-red-600 uppercase"
                    style={{ 
                      fontFamily: 'Fredoka, sans-serif',
                      textShadow: '2px 2px 0px #FCD34D'
                    }}
                  >
                    Golden Plunger!
                  </p>
                  <p className="text-2xl mt-1">🏆🪠🏆</p>
                </div>
              </div>
            )}
          </div>
          
          {/* CSS for animations */}
          <style>{`
            @keyframes fall-spin {
              0% {
                transform: translateY(0) rotate(0deg);
                opacity: 1;
              }
              100% {
                transform: translateY(100vh) rotate(720deg);
                opacity: 0.5;
              }
            }
            .animate-fall-spin {
              animation: fall-spin linear infinite;
            }
            @keyframes bounce-in {
              0% {
                transform: scale(0) rotate(-10deg);
                opacity: 0;
              }
              50% {
                transform: scale(1.2) rotate(5deg);
              }
              100% {
                transform: scale(1) rotate(0deg);
                opacity: 1;
              }
            }
            .animate-bounce-in {
              animation: bounce-in 0.6s ease-out forwards;
            }
            @keyframes slide-up {
              0% {
                transform: translateY(50px);
                opacity: 0;
              }
              100% {
                transform: translateY(0);
                opacity: 1;
              }
            }
            .animate-slide-up {
              animation: slide-up 0.5s ease-out forwards;
            }
            @keyframes trophy-pop {
              0% {
                transform: scale(0) rotate(-20deg);
                opacity: 0;
              }
              60% {
                transform: scale(1.15) rotate(5deg);
              }
              100% {
                transform: scale(1) rotate(0deg);
                opacity: 1;
              }
            }
            .animate-trophy-pop {
              animation: trophy-pop 0.7s ease-out forwards;
            }
            @keyframes wiggle {
              0%, 100% {
                transform: rotate(-3deg);
              }
              50% {
                transform: rotate(3deg);
              }
            }
            .animate-wiggle {
              animation: wiggle 0.5s ease-in-out infinite;
            }
          `}</style>
        </div>
      )}

      <Dialog open={showThankYouModal} onOpenChange={setShowThankYouModal}>
        <DialogContent className="border-4 border-black bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-green-600 text-center" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              ✅ Thanks for reporting the issue!
            </DialogTitle>
          </DialogHeader>
          <div className="text-center py-4">
            <p className="text-lg font-semibold text-gray-800 mb-6" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Want to save your contributions? Add your email to unlock badges and features!
            </p>
            <div className="flex gap-3 justify-center">
              <Button
                onClick={() => setShowThankYouModal(false)}
                variant="outline"
                className="border-2 border-gray-300 px-6"
              >
                Maybe Later
              </Button>
              <Button
                onClick={() => {
                  setShowThankYouModal(false);
                  setShowEmailPromptModal(true);
                }}
                className="bg-red-500 hover:bg-red-600 text-white font-bold px-6 border-2 border-black"
                style={{ fontFamily: 'Fredoka, sans-serif' }}
                data-testid="save-progress-btn"
              >
                Save My Progress
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Prompt Modal - shown after 5 contributions */}
      <Dialog open={showEmailPromptModal} onOpenChange={setShowEmailPromptModal}>
        <DialogContent className="border-4 border-black bg-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-red-600 text-center" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              🎉 Great job! You have {deviceStats?.total_contributions || 0} contributions!
            </DialogTitle>
            <DialogDescription className="text-gray-600 text-center">
              Add your email to save your reviews and unlock badges
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="link-name" className="font-bold text-gray-700">
                Your Name
              </Label>
              <Input
                id="link-name"
                value={emailLinkData.name}
                onChange={(e) => setEmailLinkData({...emailLinkData, name: e.target.value})}
                placeholder="Your name"
                className="mt-1 border-2 border-black"
                data-testid="link-name-input"
              />
            </div>
            <div>
              <Label htmlFor="link-email" className="font-bold text-gray-700">
                Email Address
              </Label>
              <Input
                id="link-email"
                type="email"
                value={emailLinkData.email}
                onChange={(e) => setEmailLinkData({...emailLinkData, email: e.target.value})}
                placeholder="your@email.com"
                className="mt-1 border-2 border-black"
                data-testid="link-email-input"
              />
            </div>
            <div>
              <Label htmlFor="link-password" className="font-bold text-gray-700">
                Create Password
              </Label>
              <Input
                id="link-password"
                type="password"
                value={emailLinkData.password}
                onChange={(e) => setEmailLinkData({...emailLinkData, password: e.target.value})}
                placeholder="At least 6 characters"
                className="mt-1 border-2 border-black"
                data-testid="link-password-input"
              />
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleDismissEmailPrompt}
                variant="outline"
                className="flex-1 border-2 border-gray-300"
              >
                Maybe Later
              </Button>
              <Button
                onClick={handleLinkEmail}
                disabled={linkingEmail}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white font-bold border-2 border-green-700"
                style={{ fontFamily: 'Fredoka, sans-serif' }}
                data-testid="link-email-btn"
              >
                {linkingEmail ? 'Saving...' : 'Save My Reviews'}
              </Button>
            </div>
            <p className="text-xs text-gray-500 text-center">
              Already have an account?{' '}
              <button 
                onClick={() => {
                  setShowEmailPromptModal(false);
                  navigate('/auth');
                }}
                className="text-blue-600 underline"
              >
                Log in here
              </button>
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin Login Modal */}
      <Dialog open={showLoginModal} onOpenChange={setShowLoginModal}>
        <DialogContent className="sm:max-w-md bg-white border-4 border-black">
          <DialogHeader>
            <DialogTitle className="text-red-600 font-bold text-2xl text-center" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              🔐 Admin Login
            </DialogTitle>
            <DialogDescription className="text-center text-gray-600">
              Enter your admin credentials to access the admin panel
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAdminLogin} className="space-y-4 mt-4">
            <div>
              <Label className="font-bold text-gray-700" style={{ fontFamily: 'Fredoka, sans-serif' }}>Email</Label>
              <Input
                type="email"
                value={loginData.email}
                onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                placeholder="admin@example.com"
                required
                className="border-2 border-gray-300 mt-1"
                data-testid="login-email"
              />
            </div>
            <div>
              <Label className="font-bold text-gray-700" style={{ fontFamily: 'Fredoka, sans-serif' }}>Password</Label>
              <Input
                type="password"
                value={loginData.password}
                onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                placeholder="••••••••"
                required
                className="border-2 border-gray-300 mt-1"
                data-testid="login-password"
              />
            </div>
            <Button
              type="submit"
              disabled={loggingIn}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 border-2 border-black"
              style={{ fontFamily: 'Fredoka, sans-serif' }}
              data-testid="login-submit"
            >
              {loggingIn ? 'Logging in...' : 'Login'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Admin shortcut button */}
      <AdminShortcut user={user} />
    </div>
  );
};

export default SubmitReview;
